import { useMemo } from 'react';
import type { DesignKit } from '@/webapp/runtime/kits';
import { kitTokens, rnFontFamily, px } from '@/webapp/runtime/kitTokens';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { mockupScreens } from '@/webapp/plan/mockupScreens';

/**
 * Somebody's own app, drawn in one kit, before the app exists — the browser half.
 *
 * The native app got this first; this is the same idea against the same shared functions
 * (`kitTokens`, `mockupScreens`), so the two clients cannot show different pictures of the
 * same plan. What differs is only the drawing: CSS here, StyleSheet there.
 *
 * The browser has an advantage the phone does not — it can use the kit's font stack exactly
 * as written, because a stack is what CSS wants. `rnFontFamily` is still used for the LABEL
 * of the face so the two clients agree on what they are claiming to show, but the mockup
 * itself gets the real declaration.
 *
 * What is honestly approximate: the CONTENT. The rows are the plan's real field and screen
 * names; their arrangement is a sketch, because the generator decides the real layout.
 */
export default function KitMockup({
  kit,
  plan,
  width = 100,
}: {
  kit: DesignKit;
  plan: AppPlan;
  width?: number;
}) {
  const t = useMemo(() => kitTokens(kit), [kit]);
  const frames = useMemo(() => mockupScreens(plan), [plan]);

  const uppercase = (t['display-case'] ?? '').trim() === 'uppercase';
  const radius = px(t.radius, 10);
  const pad = Math.max(6, px(t.pad, 16) / 2.6);

  // Height follows the content: a phone-shaped frame holding three rows is mostly empty, and
  // empty reads as an unfinished render rather than as a screen with room on it.
  const ROW_H = 19;
  const maxRows = Math.max(...frames.map((f) => f.rows.length));
  const hasAction = frames.some((f) => f.action);
  const height = 14 + (uppercase ? 9 : 11) + 5 + 6 + maxRows * ROW_H + (hasAction ? 19 : 0) + ROW_H;

  return (
    <div className="kit-strip" aria-label={`${kit.label}: ${kit.blurb}`}>
      {frames.map((frame, i) => (
        <div className="kit-frame-wrap" key={frame.id}>
          <div
            className="kit-frame"
            style={{
              width,
              height,
              background: t.bg,
              borderRadius: Math.min(16, radius + 6),
              borderColor: t.line,
            }}
          >
            <div
              className="kit-frame-title"
              style={{
                fontFamily: t['font-display'],
                fontWeight: Number(t['display-weight']) || 600,
                color: t.ink,
                fontSize: uppercase ? 9 : 11,
                letterSpacing: uppercase ? '0.04em' : undefined,
                textTransform: uppercase ? 'uppercase' : 'none',
              }}
            >
              {frame.title}
            </div>
            <div className="kit-frame-rule" style={{ background: t.line }} />

            {frame.rows.map((row, r) => (
              <div
                className="kit-row"
                key={`${frame.id}-${r}`}
                style={{
                  background: frame.kind === 'form' ? 'transparent' : t.surface,
                  borderRadius: Math.min(10, radius),
                  border: frame.kind === 'form' ? `1px solid ${t.line}` : '1px solid transparent',
                  paddingInline: pad / 1.6,
                }}
              >
                <span style={{ fontFamily: t['font-body'], color: t.ink, fontSize: 7.5 }}>{row}</span>
                {frame.kind === 'list' && (
                  <span className="kit-pill" style={{ background: t['accent-wash'] }}>
                    <span className="kit-dot" style={{ background: t.accent }} />
                  </span>
                )}
              </div>
            ))}

            {frame.action && (
              <div
                className="kit-cta"
                style={{
                  background: t.accent,
                  color: t['accent-ink'],
                  borderRadius: Math.min(10, radius),
                  fontFamily: t['font-display'],
                  textTransform: uppercase ? 'uppercase' : 'none',
                }}
              >
                {frame.action}
              </div>
            )}
          </div>

          {/* The flow — what makes this a journey rather than three unrelated cards. */}
          {i < frames.length - 1 && <span className="kit-arrow" style={{ background: t['ink-muted'] }} />}
        </div>
      ))}
    </div>
  );
}

/** The face this kit will actually resolve to, for anywhere that needs to name it. */
export function kitFaceName(kit: DesignKit): string {
  return rnFontFamily(kitTokens(kit)['font-display']);
}
